import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone';
import {
  Box,
  Text,
  Heading,
  Divider,
  StatusIndicator,
  TextButton,
} from '@wix/design-system';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntersect } from '@wix/experts-common';
import Star from '../Images/Star';
import ErrorFallback from '../ErrorFallback';
import { ReviewData } from '../../common/types';
import Review from './Review';
import s from './Reviews.module.scss';

type ReviewsProps = {
  showReviewsCount?: boolean;
  reviewsCount: number;
  averageReviewRating: number;
  reviews: ReviewData[];
  urlCtaKey: string;
  cursor?: string;
  noReviewsText: string;
  services?: any[];
  isMyProfile?: boolean;
  fetchMoreOnScroll?: boolean;
  borderContainerClass?: string;
  marginBottom?: string;
  isMobileView: boolean;
  fetchNextReviews: () => Promise<void>;
  writeReplyToReview: (
    id: string,
    replyBody: string,
    revision?: Long.Long,
  ) => Promise<void>;
};

const Reviews: FC<ReviewsProps> = ({
  showReviewsCount = true,
  reviewsCount,
  averageReviewRating,
  reviews,
  urlCtaKey,
  cursor,
  noReviewsText,
  isMyProfile,
  services,
  fetchMoreOnScroll = true,
  borderContainerClass,
  marginBottom,
  isMobileView,
  fetchNextReviews,
  writeReplyToReview,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ref, entry] = useIntersect({ rootMargin: '0px' }) as any;

  useEffect(() => {
    if (
      !isLoading &&
      entry.intersectionRatio === 1 &&
      cursor &&
      fetchMoreOnScroll
    ) {
      setIsLoading(true);
      fetchNextReviews()
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
    }
  }, [entry, isLoading, cursor, fetchNextReviews]);

  return (
    <Box display="block" dataHook="reviews-section" minHeight="486px">
      <Box direction="vertical" marginBottom={marginBottom}>
        {showReviewsCount && (
          <Heading
            size={isMobileView ? 'medium' : 'large'}
            as="h2"
            dataHook="reviews-title"
          >
            {t('profile-page-reviews-section-title')}
            {reviewsCount > 0
              ? ` (${Intl.NumberFormat(language, {}).format(reviewsCount)})`
              : ''}
          </Heading>
        )}

        {reviewsCount > 0 && (
          <Box marginTop="SP3">
            <Box verticalAlign="middle" marginRight="SP1">
              <Star
                className={isMobileView ? s.reviewStarMobile : s.reviewStar}
              />
            </Box>
            <Box verticalAlign="bottom">
              <Heading size={isMobileView ? 'large' : 'extraLarge'} as="span">
                {Intl.NumberFormat(language, {
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }).format(averageReviewRating)}
              </Heading>
              <Box paddingBottom={isMobileView ? '0' : '4px'}>
                <Text className={s.textColor}>&nbsp;/&nbsp;5</Text>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {reviews?.length > 0 ? (
        <>
          {isMobileView && showReviewsCount && <Divider />}
          <Box
            dataHook="reviews-container"
            display="block"
            className={borderContainerClass}
          >
            {reviews.map((review: ReviewData, index: number) => (
              <Box display="block" paddingTop="SP4" key={index}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Review
                    urlCtaKey={urlCtaKey}
                    review={review}
                    isMyProfile={isMyProfile}
                    services={services}
                    writeReplyToReview={writeReplyToReview}
                  />
                  {index + 1 < reviews.length ? (
                    <Box display="block" paddingTop="SP4">
                      <Divider />
                    </Box>
                  ) : (
                    <Box display="block" paddingTop="SP4"></Box>
                  )}
                </ErrorBoundary>
              </Box>
            ))}
            {!fetchMoreOnScroll && reviewsCount > reviews.length && (
              <Box paddingTop="30px" paddingBottom="42px">
                <TextButton
                  underline="always"
                  skin="dark"
                  onClick={fetchNextReviews}
                >
                  {t('show-more-text-button')}
                </TextButton>
              </Box>
            )}
          </Box>
          {cursor && fetchMoreOnScroll && (
            <div ref={ref} data-hook="infiniteScroll-container">
              <Box marginTop="26px" align="center" verticalAlign="middle">
                <StatusIndicator status="loading" />
              </Box>
            </div>
          )}
        </>
      ) : (
        <Box marginTop={borderContainerClass ? '-18px' : '18px'}>
          <Text size="small" weight="thin" dataHook="no-reviews-text">
            {noReviewsText}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default Reviews;
